import axios from "axios";
import {
  getStoredToken,
  clearStoredToken,
} from "@/lib/accessApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const client = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

/* ────────────────────  Access-gate interceptors  ──────────────────── */
// Phase 6 — every outbound request automatically picks up the current
// invite-token bearer from localStorage. The token is captured from the
// URL on first visit by `<AccessGate>` and persisted via `setStoredToken`.
client.interceptors.request.use((config) => {
  const tok = getStoredToken();
  if (tok) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tok}`;
  }
  return config;
});

// A 401 means the token in localStorage is no longer valid (rotated by an
// admin, or never was valid). We wipe the bad token and broadcast a
// custom event so the top-level <AccessGate> can re-render the invite
// screen — no full page reload required, no auth state in React Router.
client.interceptors.response.use(
  (resp) => resp,
  (err) => {
    if (err?.response?.status === 401) {
      clearStoredToken();
      try {
        window.dispatchEvent(new CustomEvent("access-token-invalidated"));
      } catch (dispatchErr) {
        // SSR safety — `window` exists in the browser only. We catch
        // because some test runners and the Expo SSR pre-render
        // step don't have DOM events. The auth state recovery still
        // works on the next render via the localStorage clear above.
        console.warn("apiClient: failed to dispatch access-token-invalidated", dispatchErr);
      }
    }
    return Promise.reject(err);
  },
);

// ── Library CRUD ────────────────────────────────────────────────
export const listLibrary = (lib) => client.get(`/lib/${lib}`).then((r) => r.data);

export const getLibraryItem = (lib, id) =>
  client.get(`/lib/${lib}/${id}`).then((r) => r.data);

export const createLibraryItem = (lib, data) =>
  client.post(`/lib/${lib}`, data).then((r) => r.data);

export const updateLibraryItem = (lib, id, data) =>
  client.put(`/lib/${lib}/${id}`, data).then((r) => r.data);

export const deleteLibraryItem = (lib, id) =>
  client.delete(`/lib/${lib}/${id}`);

export const seedLibraries = (force = false) =>
  client.post(`/lib/seed?force=${force}`).then((r) => r.data);

// ── Health ──────────────────────────────────────────────────────
export const getHealth = () => client.get("/").then((r) => r.data);

export { API, BACKEND_URL };
export default client;
